import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { rentalsService } from '../services/api';
import { AppNav } from '../components/AppNav';

interface Period {
  id: string;
  month: string;
  due_date: string;
  amount: number;
  status: string;
  paid_at: string | null;
  paid_amount: number | null;
  notes: string | null;
  is_overdue?: boolean;
}

interface Lease {
  id: string;
  status: string;
  monthly_rent: number;
  start_date: string;
  end_date: string;
  rent_day: number;
  deposit?: number | null;
  tenant_name?: string;
  tenant_whatsapp?: string | null;
  unit_name?: string;
  property_title?: string;
  property_address?: string;
  periods: Period[];
  increases: Array<{ id: string; effective_date: string; new_amount: number; percentage: number | null; reason: string | null }>;
}

const STATUS_ES: Record<string, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  overdue: 'Vencido',
  waived: 'Condonado',
};

const LEASE_STATUS_ES: Record<string, string> = {
  active: 'Activo',
  ended: 'Terminado',
  cancelled: 'Cancelado',
};

const money = (n: number) => `$${n.toLocaleString('es-MX')}`;

// QA 2026-10-23: el backend responde 4xx con `detail` en inglés; aquí se
// traduce lo accionable en vez de un "no se pudo" genérico.
function apiError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail !== 'string') return fallback;
  if (detail.includes('within the lease term')) return 'La fecha del incremento debe estar dentro de la vigencia del contrato.';
  if (detail.includes('after the last scheduled increase')) return 'La fecha debe ser posterior al último incremento programado.';
  if (detail.includes('already paid')) return 'Ese periodo ya estaba pagado.';
  if (detail.includes('Only active leases')) return 'Solo un arrendamiento activo puede terminarse o cancelarse.';
  return fallback;
}

export default function LeaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [lease, setLease] = useState<Lease | null>(null);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [increase, setIncrease] = useState({ new_amount: '', effective_date: '', reason: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    rentalsService
      .getLease(id!)
      .then(setLease)
      .catch(() => setError('No se pudo cargar el arrendamiento.'));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Gate R1: los returns tempranos también llevan AppNav (antes el usuario
  // quedaba atrapado en "No se pudo cargar…" sin Volver ni Salir).
  if (error) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <AppNav />
        <div className="p-8 text-sm text-destructive">{error}</div>
      </div>
    );
  }
  if (!lease) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <AppNav />
        <div className="p-8 text-sm text-muted-foreground">Cargando…</div>
      </div>
    );
  }

  const isActive = lease.status === 'active';
  // Fecha LOCAL (en-CA = YYYY-MM-DD); toISOString daría el día UTC
  const today = new Date().toLocaleDateString('en-CA');
  // Terminado/cancelado: se puede seguir cobrando lo adeudado, no lo futuro
  const canMarkPaid = (p: Period) =>
    p.status !== 'paid' && p.status !== 'waived' && (isActive || p.due_date <= today);

  const markPaid = async (p: Period) => {
    // N-007: un clic accidental registraba un cobro sin deshacer
    if (!window.confirm(`¿Registrar el pago de ${p.month} por ${money(p.amount)}?`)) return;
    try {
      await rentalsService.markPaid(lease.id, p.id, {});
      setActionError('');
      load();
    } catch (err) {
      setActionError(apiError(err, 'No se pudo marcar como pagado.'));
    }
  };

  const changeStatus = async (status: 'ended' | 'cancelled') => {
    const verb = status === 'ended' ? 'terminar' : 'cancelar';
    if (!window.confirm(`¿Seguro que quieres ${verb} este arrendamiento? La unidad quedará disponible y se dejarán de enviar recordatorios.`)) return;
    setBusy(true);
    try {
      await rentalsService.updateLeaseStatus(lease.id, status);
      setActionError('');
      load();
    } catch (err) {
      setActionError(apiError(err, `No se pudo ${verb} el arrendamiento.`));
    } finally {
      setBusy(false);
    }
  };

  const addIncrease = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await rentalsService.addIncrease(lease.id, {
        new_amount: Number(increase.new_amount),
        effective_date: increase.effective_date,
        reason: increase.reason.trim() || null,
      });
      setIncrease({ new_amount: '', effective_date: '', reason: '' });
      setActionError('');
      load();
    } catch (err) {
      setActionError(apiError(err, 'No se pudo registrar el incremento.'));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 text-sm bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring';

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight mb-1">
              {lease.tenant_name ?? 'Arrendamiento'} · {money(lease.monthly_rent)}/mes
            </h1>
            <p className="text-sm text-muted-foreground">
              {[lease.property_title, lease.unit_name].filter(Boolean).join(' · ')}
              {lease.property_address && ` — ${lease.property_address}`}
            </p>
            <p className="text-sm text-muted-foreground">
              {lease.start_date} → {lease.end_date} · paga el día {lease.rent_day}
              {lease.deposit != null && ` · depósito ${money(lease.deposit)}`}
              {lease.tenant_whatsapp && ` · WhatsApp ${lease.tenant_whatsapp}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-1 rounded-md bg-muted">
              {LEASE_STATUS_ES[lease.status] ?? lease.status}
            </span>
            {isActive && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => changeStatus('ended')}
                  className="text-xs border border-border px-2 py-1 rounded-md disabled:opacity-50"
                >
                  Terminar
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => changeStatus('cancelled')}
                  className="text-xs border border-destructive text-destructive px-2 py-1 rounded-md disabled:opacity-50"
                >
                  Cancelar arrendamiento
                </button>
              </>
            )}
          </div>
        </div>

        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3">
          Periodos
        </h2>
        {actionError && <p className="text-sm text-destructive mb-3">{actionError}</p>}
        <div className="space-y-2 mb-8">
          {lease.periods.map((p) => {
            const label = p.is_overdue ? 'Vencido' : STATUS_ES[p.status] ?? p.status;
            const cls = p.status === 'paid' ? 'text-green-600' : p.is_overdue || p.status === 'overdue' ? 'text-destructive' : 'text-muted-foreground';
            return (
              <div
                key={p.id}
                className="flex items-center justify-between border border-border rounded-xl px-4 py-3 bg-card text-sm"
              >
                <span>
                  <span>{p.month}</span> · {money(p.amount)} · vence {p.due_date}
                </span>
                <span className="flex items-center gap-3">
                  <span className={cls}>{label}</span>
                  {canMarkPaid(p) && (
                    <button
                      type="button"
                      onClick={() => markPaid(p)}
                      className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-md"
                    >
                      Marcar pagado
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3">
          Incrementos
        </h2>
        {lease.increases.length === 0 && (
          <p className="text-sm text-muted-foreground mb-3">Sin incrementos registrados.</p>
        )}
        <div className="space-y-2 mb-4">
          {lease.increases.map((i) => (
            <div key={i.id} className="border border-border rounded-xl px-4 py-3 bg-card text-sm">
              {i.effective_date}: {money(i.new_amount)}
              {i.percentage != null && ` (+${i.percentage}%)`} {i.reason && `· ${i.reason}`}
            </div>
          ))}
        </div>
        {isActive && (
          <form onSubmit={addIncrease} aria-label="Nuevo incremento" className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
            <label className="text-xs text-muted-foreground">
              Nueva renta (MXN)
              <input type="number" required min="1" value={increase.new_amount}
                onChange={(e) => setIncrease({ ...increase, new_amount: e.target.value })} className={inputCls} />
            </label>
            <label className="text-xs text-muted-foreground">
              A partir de
              <input type="date" required value={increase.effective_date}
                onChange={(e) => setIncrease({ ...increase, effective_date: e.target.value })} className={inputCls} />
            </label>
            <label className="text-xs text-muted-foreground">
              Motivo (opcional)
              <input type="text" value={increase.reason} placeholder="INPC"
                onChange={(e) => setIncrease({ ...increase, reason: e.target.value })} className={inputCls} />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="bg-primary text-primary-foreground py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              Agregar incremento
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
